import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  FileText, 
  User,
  Calendar,
  Eye,
  Database,
  Shield
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function AuditLogsPage() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resourceFilter, setResourceFilter] = useState("all");

  useEffect(() => {
    fetchLogs();
  }, [resourceFilter]);

  const fetchLogs = async () => {
    try {
      const params = resourceFilter !== "all" ? `?resource_type=${resourceFilter}` : "";
      const response = await apiClient.get(`/audit-logs${params}`);
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      create: "bg-green-100 text-green-700",
      update: "bg-blue-100 text-blue-700",
      delete: "bg-red-100 text-red-700",
      view: "bg-slate-100 text-slate-700",
      login: "bg-violet-100 text-violet-700",
      send: "bg-amber-100 text-amber-700"
    };
    return <Badge className={colors[action] || "bg-slate-100 text-slate-700"}>{action}</Badge>;
  };

  const getResourceIcon = (resourceType) => {
    const icons = {
      auth: Shield,
      patient: User,
      pta_test: Eye,
      clinic: Database,
      appointment: Calendar,
      recommendation: FileText
    };
    const Icon = icons[resourceType] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  const resourceTypes = ["all", "auth", "patient", "pta_test", "clinic", "appointment", "recommendation", "hearing_aid", "whatsapp_message"];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Audit Logs</h1>
            <p className="text-slate-500">Track all system activities and access</p>
          </div>
          
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {resourceTypes.map(type => (
                <SelectItem key={type} value={type} className="capitalize">
                  {type === "all" ? "All Resources" : type.replace("_", " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {["create", "update", "view", "login"].map(action => {
            const count = logs.filter(l => l.action === action).length;
            return (
              <Card key={action}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-500 capitalize">{action} actions</p>
                  </div>
                  {getActionBadge(action)}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="skeleton h-16 rounded-lg"></div>
                ))}
              </div>
            ) : logs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="text-left p-3">Timestamp</th>
                      <th className="text-left p-3">User</th>
                      <th className="text-left p-3">Action</th>
                      <th className="text-left p-3">Resource</th>
                      <th className="text-left p-3">Resource ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id} className="table-row border-b border-slate-100" data-testid={`audit-log-${log.id}`}>
                        <td className="p-3 text-sm text-slate-500">
                          {format(new Date(log.created_at), "PPp")}
                        </td>
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{log.user_email}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {getResourceIcon(log.resource_type)}
                            <span className="text-sm text-slate-700 capitalize">
                              {log.resource_type?.replace("_", " ")}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-slate-400 font-mono">
                            {log.resource_id?.substring(0, 8) || "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No audit logs found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
